import { create } from 'zustand'
import { useCallback, useEffect } from 'react'
import type { Locale, Translations, TranslationKey } from '../../locales/types'
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY } from '../../locales/types'

type LocaleState = {
  locale: Locale
  translations: Translations | null
  isLoading: boolean
  setLocale: (locale: Locale) => void
  loadTranslations: (locale: Locale) => Promise<void>
}

const loadLocaleFile = async (locale: Locale): Promise<Translations> => {
  switch (locale) {
    case 'de':
      return (await import('../../locales/de.json')).default
    case 'af':
      return (await import('../../locales/af.json')).default
    case 'en':
    default:
      return (await import('../../locales/en.json')).default
  }
}

const getStoredLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (stored && ['en', 'de', 'af'].includes(stored)) {
    return stored as Locale
  }
  return DEFAULT_LOCALE
}

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: getStoredLocale(),
  translations: null,
  isLoading: true,

  setLocale: (locale: Locale) => {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    set({ locale })
  },

  loadTranslations: async (locale: Locale) => {
    set({ isLoading: true })
    const translations = await loadLocaleFile(locale)
    set({ translations, isLoading: false })
  },
}))

type InterpolationParams = Record<string, string | number>

const interpolate = (text: string, params?: InterpolationParams): string => {
  if (!params) return text
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replace(new RegExp(`{{${key}}}`, 'g'), String(value)),
    text
  )
}

// Helper to get nested value by dot-separated path
const getNestedValue = (obj: Record<string, unknown>, path: string): string | undefined => {
  const keys = path.split('.')
  let current: unknown = obj

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined
    }
    current = (current as Record<string, unknown>)[key]
  }

  return typeof current === 'string' ? current : undefined
}

export function useTranslation() {
  const { locale, translations, isLoading, setLocale, loadTranslations } = useLocaleStore()

  useEffect(() => {
    loadTranslations(locale)
  }, [locale, loadTranslations])

  const t = useCallback(
    (key: TranslationKey, params?: InterpolationParams): string => {
      if (!translations) return key

      const text = getNestedValue(translations as unknown as Record<string, unknown>, key)

      if (!text) {
        console.warn(`Translation missing: ${key}`)
        return key
      }

      return interpolate(text, params)
    },
    [translations]
  )

  const changeLocale = useCallback(
    (newLocale: Locale) => {
      setLocale(newLocale)
    },
    [setLocale]
  )

  return {
    t,
    locale,
    changeLocale,
    isLoading,
  }
}
